import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'boro-gymnastiko-meta-tatouaz-athina';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Μπορώ να γυμναστώ μετά από τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι αμέσως. Πρέπει να περιμένεις τουλάχιστον σαράντα οκτώ ώρες για ελαφριά δραστηριότητα, μια εβδομάδα για έντονη προπόνηση και δύο εβδομάδες για αθλήματα επαφής ή κολύμπι. Το ακριβές διάστημα εξαρτάται από το τατουάζ σου." } },
    { "@type": "Question", name: "Γιατί δεν πρέπει να ιδρώσω πάνω σε φρέσκο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ο ιδρώτας μαζί με τα βακτήρια του δέρματος και του γυμναστηρίου δημιουργεί ιδανικές συνθήκες για μόλυνση. Επιπλέον, το αλάτι του ιδρώτα ερεθίζει το τραυματισμένο δέρμα και επιβραδύνει την επούλωση." } },
    { "@type": "Question", name: "Μπορώ να σηκώσω βάρη με φρέσκο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Απόφυγε την προπόνηση με βάρη για τουλάχιστον μία με δύο εβδομάδες. Το τέντωμα του δέρματος, ο ιδρώτας και η επαφή με τον εξοπλισμό μπορεί να χαλάσουν το τατουάζ και να αυξήσουν τον κίνδυνο μόλυνσης." } },
    { "@type": "Question", name: "Μπορώ να τρέξω μετά από τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Για ελαφρύ τρέξιμο, μερικές μέρες αναμονής μπορεί να αρκούν. Για έντονο τρέξιμο ή HIIT, δώσε τουλάχιστον μία εβδομάδα, ειδικά αν τρέχεις έξω στην Αθήνα με τον ήλιο." } },
    { "@type": "Question", name: "Πού μπορώ να κάνω τατουάζ στην Αθήνα με σωστή καθοδήγηση;", acceptedAnswer: { "@type": "Answer", text: "Το 210 Tattoo Athens προσφέρει έμπειρους καλλιτέχνες, αυστηρή υγιεινή και εξατομικευμένες οδηγίες φροντίδας. Στείλε μήνυμα στο @210tattoo στο Instagram για να κλείσεις ραντεβού." } },
  ],
};

export default function BoroGymnastikoMetaTatouazAthina() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Μπορώ να Γυμναστώ μετά από Τατουάζ;",
      "description": "Οδηγός για την Αθήνα: πόσο πρέπει να περιμένεις πριν το γυμναστήριο μετά από τατουάζ, οι κίνδυνοι ανά τύπο άσκησης και πώς να επιστρέψεις σταδιακά στη ρουτίνα σου.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-07",
      "dateModified": "2026-07-07",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-gym-faq', faqSchema);
    inject('schema-gym-article', articleSchema);
    return () => {
      document.getElementById('schema-gym-faq')?.remove();
      document.getElementById('schema-gym-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Γυμναστική μετά από Τατουάζ; | 210 Tattoo Athens</title>
        <meta name="description" content="Μπορώ να γυμναστώ μετά από τατουάζ; Πόσο πρέπει να περιμένεις, οι κίνδυνοι ανά τύπο άσκησης και πώς να επιστρέψεις σταδιακά στη ρουτίνα σου στην Αθήνα." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Γυμναστική μετά από Τατουάζ; | 210 Tattoo Athens" />
        <meta property="og:description" content="Πόσο να περιμένεις πριν το γυμναστήριο και πώς να επιστρέψεις σταδιακά." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Γυμναστική μετά από Τατουάζ; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Πόσο να περιμένεις, οι κίνδυνοι ανά άσκηση και σταδιακή επιστροφή." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Γυμναστική μετά από Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Μπορώ να γυμναστώ</span>
              <br />
              <span className="text-white">μετά από τατουάζ;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">7 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Το γυμναστήριο και το φρέσκο τατουάζ δεν πάνε καλά μαζί, και υπάρχουν συγκεκριμένοι λόγοι. Δες πόσο πρέπει να περιμένεις, ποιοι τύποι άσκησης είναι πιο επικίνδυνοι και πώς να επιστρέψεις χωρίς να ρισκάρεις το κομμάτι σου.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Risks */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Οι κίνδυνοι της γυμναστικής <span className="text-pink-500">με φρέσκο τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ένα φρέσκο τατουάζ είναι μια ανοιχτή πληγή. Η γυμναστική δημιουργεί συνθήκες που δουλεύουν εναντίον της επούλωσης με πολλούς τρόπους.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Ο ιδρώτας και η μόλυνση', desc: 'Ο ιδρώτας μαζί με τα βακτήρια δημιουργεί το τέλειο σκηνικό για μόλυνση, ενώ το αλάτι ερεθίζει το τραυματισμένο δέρμα, όπως και το θαλασσινό νερό.' },
                { title: 'Η τριβή και η ζημιά στο σχέδιο', desc: 'Η επαφή με βάρη, μηχανήματα ή ρούχα μπορεί να ξεκολλήσει πρόωρα τις φλούδες, να αφαιρέσει μελάνι και να δημιουργήσει κενά στο σχέδιο.' },
                { title: 'Η ένταση του δέρματος', desc: 'Όταν οι μύες τεντώνονται, το δέρμα από πάνω τεντώνεται μαζί τους. Οι λεπτές γραμμές μπορεί να απλωθούν και οι λεπτομέρειες να χάσουν την καθαρότητά τους.' },
                { title: 'Το βακτηριακό περιβάλλον', desc: 'Τα γυμναστήρια είναι χώροι όπου εκατοντάδες άνθρωποι ιδρώνουν στον ίδιο εξοπλισμό. Για μια ανοιχτή πληγή, αυτό είναι σοβαρός κίνδυνος.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Αν κάτι δεν πάει καλά κατά την επούλωση, δες τα σημάδια στον οδηγό μας για το <Link to="/blog/pos-katalabaineis-tatouaz-molynsi-athina" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί</Link>.
            </p>
          </div>
        </section>

        {/* How long to wait */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πόσο πρέπει να περιμένω <span className="text-pink-500">πριν πάω γυμναστήριο;</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ο γενικός κανόνας είναι τουλάχιστον σαράντα οκτώ ώρες πριν από έντονη άσκηση, αλλά ο πραγματικός χρόνος εξαρτάται από το μέγεθος, το σημείο και τον τύπο της άσκησης.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Βάρη και προπόνηση δύναμης', desc: 'Από τις πιο απαιτητικές δραστηριότητες. Περίμενε τουλάχιστον μία με δύο εβδομάδες, ειδικά αν το τατουάζ είναι στο πάνω μέρος του σώματος.' },
                { title: 'Cardio και τρέξιμο', desc: 'Για ελαφρύ cardio, μερικές μέρες. Για έντονο τρέξιμο ή HIIT, τουλάχιστον μία εβδομάδα, ειδικά έξω με τον ήλιο και τη σκόνη.' },
                { title: 'Yoga και pilates', desc: 'Δεν είναι τόσο ήπια όσο νομίζεις. Έντονο τέντωμα και επαφή με στρώμα. Απόφυγέ τα για τουλάχιστον μία εβδομάδα.' },
                { title: 'Κολύμπι και υδάτινα αθλήματα', desc: 'Απόλυτα εκτός για τουλάχιστον δύο με τρεις εβδομάδες. Το παρατεταμένο μούλιασμα σε νερό είναι σοβαρός κίνδυνος.' },
                { title: 'Αθλήματα επαφής', desc: 'Ποδόσφαιρο, μπάσκετ, πολεμικές τέχνες. Χρειάζεσαι τουλάχιστον δύο εβδομάδες λόγω τραυματισμού, ιδρώτα και επαφής.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Το κολύμπι αξίζει ιδιαίτερη προσοχή το καλοκαίρι στην Αθήνα. Δες πότε μπορείς να πας <Link to="/blog/sea-after-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">θάλασσα μετά από τατουάζ</Link>.
            </p>
          </div>
        </section>

        {/* Gradual return */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να επιστρέψεις <span className="text-pink-500">σιγά σιγά στο γυμναστήριο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Όταν έρθει η ώρα, μην πηδήξεις κατευθείαν στην πλήρη ρουτίνα σου. Μια σταδιακή επιστροφή προστατεύει το τατουάζ.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Ξεκίνα με ελαφριές δραστηριότητες', desc: 'Ιδανικά χωρίς πολύ ιδρώτα και χωρίς άμεση επαφή του τατουάζ με εξοπλισμό.' },
                { title: 'Απόφυγε το έντονο τέντωμα', desc: 'Ασκήσεις που τεντώνουν έντονα το δέρμα στην περιοχή του τατουάζ.' },
                { title: 'Φόρα καθαρά, χαλαρά ρούχα', desc: 'Που δεν τρίβουν την περιοχή, και χρησιμοποίησε καθαρή πετσέτα.' },
                { title: 'Φρόντισε το αμέσως μετά', desc: 'Καθάρισε απαλά το τατουάζ και βάλε την ενυδατική που σου έχει προτείνει ο καλλιτέχνης σου.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Αν παρατηρήσεις έντονο κοκκίνισμα, πρήξιμο ή ενόχληση μετά την προπόνηση, επέστρεψες πολύ νωρίς. Δώσε άλλη μια εβδομάδα. Για πλήρεις οδηγίες, δες τον <Link to="/blog/tattoo-aftercare-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">οδηγό μας για τη φροντίδα μετά το studio</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θες να προγραμματίσεις το τατουάζ σου <span className="text-pink-500">γύρω από τη γυμναστική;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μήνυμα στο Instagram του στούντιο, περίγραψε την ιδέα σου, πες τους για τη ρουτίνα σου και η ομάδα θα σε καθοδηγήσει στη σωστή προσέγγιση.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/boro-tatouaz-an-eimai-arrostos-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τατουάζ όταν Είσαι Άρρωστος;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Γιατί χρειάζεται αναβολή και πώς να προγραμματίσεις σωστά.</p>
              </Link>
              <Link to="/blog/sea-after-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Μπορώ να πάω Θάλασσα μετά από Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πόσο να περιμένεις πριν πας θάλασσα με φρέσκο τατουάζ.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/sleeve-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Sleeve Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
